import React from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ChatMessage = ({ message, onSuggestionClick, onActionClick }) => {
  const isUser = message?.sender === 'user';
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`flex gap-3 md:gap-4 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4 md:mb-6`}>
      <div className="flex-shrink-0">
        {isUser ? (
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm md:text-base">
            {message?.userInitials || 'AD'}
          </div>
        ) : (
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <Icon name="Bot" size={20} color="#FFFFFF" />
          </div>
        )}
      </div>
      <div className={`flex-1 max-w-[85%] md:max-w-[75%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-2`}>
        <div className={`rounded-2xl px-4 py-3 md:px-5 md:py-4 ${
          isUser 
            ? 'bg-primary text-primary-foreground rounded-tr-sm' 
            : 'bg-card border border-border rounded-tl-sm'
        }`}>
          <p className="text-sm md:text-base leading-relaxed whitespace-pre-line">
            {message?.content}
          </p>

          {message?.recommendations && message?.recommendations?.length > 0 && (
            <div className="mt-4 space-y-3">
              {message?.recommendations?.map((rec, index) => (
                <div 
                  key={index}
                  className="bg-muted rounded-xl p-3 md:p-4 border border-border hover:border-primary transition-all cursor-pointer"
                  onClick={() => onActionClick && onActionClick('view-field', rec)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={rec?.icon} size={20} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm md:text-base text-foreground mb-1">
                        {rec?.title}
                      </h4>
                      <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">
                        {rec?.description}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="inline-flex items-center gap-1 text-xs text-primary font-medium">
                          <Icon name="TrendingUp" size={14} />
                          {rec?.matchScore}% compatible
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {message?.quickActions && message?.quickActions?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {message?.quickActions?.map((action, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  iconName={action?.icon}
                  iconPosition="left"
                  onClick={() => onActionClick && onActionClick(action?.type, action?.data)}
                  className="text-xs md:text-sm"
                >
                  {action?.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {message?.suggestions && message?.suggestions?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {message?.suggestions?.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => onSuggestionClick && onSuggestionClick(suggestion)}
                className="px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-muted hover:bg-muted/80 text-xs md:text-sm text-foreground border border-border transition-all hover:border-primary"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        <span className="text-xs text-muted-foreground px-2">
          {formatTime(message?.timestamp)}
          {message?.status && (
            <Icon 
              name={message?.status === 'sent' ? 'Check' : 'CheckCheck'} 
              size={14} 
              className="inline ml-1"
              color={message?.status === 'read' ? 'var(--color-primary)' : 'currentColor'}
            />
          )}
        </span>
      </div>
    </div>
  );
};

export default ChatMessage;